import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";
import { UserRole, UserStatus } from "../types";
import { ACCESS_ROLE_OPTIONS, ACCESS_STATUS_OPTIONS, getEffectivePermissions, getRoleLabel, getStatusLabel, getStatusToneClasses, normalizeCustomPermissions, PERMISSION_GROUPS, type PermissionKey } from "../lib/accessControl";
import { cn } from "../utils";

interface UserAccessEditorProps {
  userRecord: any;
  onSaveAccess: (updates: { role: UserRole; status: UserStatus; customPermissions: PermissionKey[] }) => Promise<void>;
}

export function UserAccessEditor({ userRecord, onSaveAccess }: UserAccessEditorProps) {
  const [role, setRole] = useState<UserRole>(userRecord?.role || "standard");
  const [status, setStatus] = useState<UserStatus>(userRecord?.status || "active");
  const [customPermissions, setCustomPermissions] = useState<PermissionKey[]>(normalizeCustomPermissions(userRecord?.customPermissions));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRole(userRecord?.role || "standard");
    setStatus(userRecord?.status || "active");
    setCustomPermissions(normalizeCustomPermissions(userRecord?.customPermissions));
  }, [userRecord]);

  const effectivePermissions = useMemo(
    () => getEffectivePermissions(role, customPermissions),
    [role, customPermissions]
  );

  const hasChanges =
    role !== (userRecord?.role || "standard") ||
    status !== (userRecord?.status || "active") ||
    JSON.stringify([...customPermissions].sort()) !== JSON.stringify([...normalizeCustomPermissions(userRecord?.customPermissions)].sort());

  const togglePermission = (permissionKey: PermissionKey) => {
    setCustomPermissions((current) =>
      current.includes(permissionKey)
        ? current.filter((permission) => permission !== permissionKey)
        : [...current, permissionKey]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveAccess({ role, status, customPermissions });
      toast.success("Access settings updated");
    } catch (error: any) {
      toast.error(error?.message || "Failed to save access settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-950/60 p-4">
        <img
          src={userRecord?.photoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(userRecord?.name || "User")}&background=f1f5f9&color=64748b`}
          alt={userRecord?.name || "User"}
          className="h-12 w-12 rounded-xl object-cover"
          referrerPolicy="no-referrer"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{userRecord?.name || "Unknown User"}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">{userRecord?.email || "No email recorded"}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="inline-flex items-center rounded-full bg-white dark:bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
              {getRoleLabel(role)}
            </span>
            <span className={cn("inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold border", getStatusToneClasses(status))}>
              {getStatusLabel(status)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white dark:bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
              <Shield className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
              {effectivePermissions.length} effective permissions
            </span>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Access Role</label>
          <select
            value={role}
            onChange={(event) => setRole(event.target.value as UserRole)}
            className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2.5 text-sm text-slate-800 dark:text-slate-100 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
          >
            {ACCESS_ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {ACCESS_ROLE_OPTIONS.find((option) => option.value === role)?.description}
          </p>
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Account Status</label>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value as UserStatus)}
            className={cn("w-full rounded-xl px-3 py-2.5 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20", getStatusToneClasses(status))}
          >
            {ACCESS_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {ACCESS_STATUS_OPTIONS.find((option) => option.value === status)?.description}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Additional Permissions</h4>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Role permissions are applied automatically. Use these toggles for targeted extra access.
          </p>
        </div>

        <div className="grid gap-4">
          {PERMISSION_GROUPS.map((group) => (
            <div key={group.id} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4">
              <h5 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{group.label}</h5>
              <div className="mt-3 grid gap-3">
                {group.permissions.map((permission) => {
                  const enabled = customPermissions.includes(permission.key);
                  const inherited = effectivePermissions.includes(permission.key) && !enabled;
                  return (
                    <label
                      key={permission.key}
                      className={cn(
                        "flex items-start gap-3 rounded-xl border p-3 transition-colors",
                        enabled
                          ? "border-indigo-200 dark:border-indigo-900/50 bg-indigo-50/60 dark:bg-indigo-950/30"
                          : inherited
                            ? "border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/60 dark:bg-emerald-950/20"
                            : "border-slate-200 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/60"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={() => togglePermission(permission.key)}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{permission.label}</span>
                          {inherited && (
                            <span className="rounded-full bg-white dark:bg-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/50">
                              Included by role
                            </span>
                          )}
                          {enabled && (
                            <span className="rounded-full bg-white dark:bg-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900/50">
                              Extra grant
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{permission.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4">
        <div className="text-xs text-slate-500 dark:text-slate-400">
          These access settings are linked to the main People &amp; HR record for this person.
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={cn(
            "inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors",
            !hasChanges || saving
              ? "cursor-not-allowed bg-slate-100 text-slate-400"
              : "bg-indigo-600 text-white hover:bg-indigo-700"
          )}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          Save Access
        </button>
      </div>
    </div>
  );
}
